import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Download, Eye, Plus, Trash2, Loader2, X, Layers, LayoutGrid, ArrowLeft, Sparkles } from 'lucide-react';
import { 
  createInspirationalDesign, 
  getInspirationalDesigns, 
  updateInspirationalDesign, 
  deleteInspirationalDesign 
} from '../api/inspirationalDesign.api';
import { createThumbnailImageFile, uploadImageToS3 } from '../utils/s3Upload';
import { FEMALE_INSPIRATION_DESIGNS } from '../data/femaleInspirationDesigns';
import { analyzeGarment } from '../api/garmentAnalysis.api';
import GarmentDesignDetailView from './GarmentDesignDetailView';
import LazyImage from './LazyImage';
import ProgressiveGrid from './ProgressiveGrid';
import { normalizeImageUrl } from '../utils/cdn';
import {
  buildVirtualGarmentCollections,
  groupVirtualCollectionsByCategory,
  formatGarmentCategoryLabel,
  designOwnerLabel,
} from '../utils/garmentTypeCollections';
import BlobPreview from './BlobPreview';

function formatProductCategoryLabel(value) {
  return formatGarmentCategoryLabel(value);
}

function formatProductTypeDisplay(ga) {
  if (!ga) return '—';
  if (ga.productType === 'other' && ga.otherTypeDescription) {
    return `Other (${ga.otherTypeDescription})`;
  }
  if (ga.productType === 'other') return 'Other';
  return ga.productType ? ga.productType.replace(/-/g, ' ') : '—';
}

function formatImageKind(ik) {
  if (ik === 'real_design') return 'Real / photo';
  if (ik === 'sketch') return 'Sketch / flat';
  return ik || '—';
}

function getCardImageSrc(design) {
  const raw = design?.thumbnailUrl || design?.imageUrl || '';
  return raw ? normalizeImageUrl(raw) : '';
}

function GarmentCardImage({ design, className = 'relative w-full h-full', imgClassName = 'w-full h-full object-cover' }) {
  const src = getCardImageSrc(design);
  if (!src) {
    return (
      <div className={`${className} flex items-center justify-center bg-gray-100 text-gray-400 text-sm`}>
        No Image
      </div>
    );
  }
  return (
    <LazyImage
      src={src}
      alt={design?.productName || design?.name || ''}
      className={className}
      imgClassName={imgClassName}
      allowFallback={false}
    />
  );
}

function buildThumbnailFileName(fileName) {
  const baseName = String(fileName || 'garment').replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_');
  return `${baseName}-${Date.now()}-thumbnail.jpg`;
}

function buildGarmentFileName(fileName) {
  const str = String(fileName || 'garment');
  const lastDot = str.lastIndexOf('.');
  const ext = lastDot >= 0 ? str.slice(lastDot + 1).toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg' : 'jpg';
  const base = (lastDot >= 0 ? str.slice(0, lastDot) : str).replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 60) || 'garment';
  return `garment-${Date.now()}-${base}.${ext}`;
}

function getFileBaseName(file, fallback = 'Garment design') {
  return String(file?.name || fallback).replace(/\.[^/.]+$/, '').trim() || fallback;
}

const EMPTY_NEW_DESIGN = {
  imageFile: null,
  imageFiles: []
};

function GarmentAnalysisSummary({ analysis }) {
  if (!analysis || !analysis.productCategory) return null;
  return (
    <div className="mt-2 pt-2 border-t border-purple-50/80 space-y-1.5 text-xs">
      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 font-medium">
          {formatProductCategoryLabel(analysis.productCategory)}
        </span>
        <span
          className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-medium max-w-[11rem] truncate"
          title={formatProductTypeDisplay(analysis)}
        >
          {formatProductTypeDisplay(analysis)}
        </span>
        <span className="inline-flex px-2 py-0.5 rounded-full bg-amber-50 text-amber-900 font-medium">
          {formatImageKind(analysis.imageKind)}
        </span>
        {analysis.confidence ? (
          <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
            {analysis.confidence}
          </span>
        ) : null}
      </div>
      {analysis.rationale ? (
        <p className="text-gray-500 line-clamp-2 leading-snug" title={analysis.rationale}>
          {analysis.rationale}
        </p>
      ) : null}
    </div>
  );
}

const InspirationalDesignsView = ({
  publicBrowseOnly = false,
  showNotification = null,
  setActiveView = null,
  setProductDetails = null,
}) => {
  const [viewMode, setViewMode] = useState('grid');
  const [designs, setDesigns] = useState([]);
  const [myInspirations, setMyInspirations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploadingDesign, setUploadingDesign] = useState(false);
  const [isAdmin, setIsAdmin] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('role')?.toLowerCase() === 'admin'
  );
  const [visibilityLoadingId, setVisibilityLoadingId] = useState(null);

  const [garmentPageTab, setGarmentPageTab] = useState('designs'); // 'designs' | 'collections'
  const [selectedCollectionKey, setSelectedCollectionKey] = useState(null);
  const [showEmptyGarmentTypes, setShowEmptyGarmentTypes] = useState(false);
  const [reanalyzingId, setReanalyzingId] = useState(null);
  const [garmentDetailId, setGarmentDetailId] = useState(null);

  // Form state for adding new inspiration
  const [newDesign, setNewDesign] = useState({
    ...EMPTY_NEW_DESIGN
  });
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ current: 0, total: 0 });
  const [currentUploadLabel, setCurrentUploadLabel] = useState('');

  const loadCancelledRef = useRef(false);

  useEffect(() => {
    loadCancelledRef.current = false;
    const userRole = localStorage.getItem('role')?.toLowerCase();
    setIsAdmin(userRole === 'admin');

    if (publicBrowseOnly) {
      loadDesigns();
    } else {
      setLoading(true);
      loadMyInspirations()
        .finally(() => {
          if (!loadCancelledRef.current) setLoading(false);
        });
    }

    return () => {
      loadCancelledRef.current = true;
    };
  }, [publicBrowseOnly]);

  const loadMyInspirations = async () => {
    try {
      const userDesigns = await getInspirationalDesigns();
      if (!loadCancelledRef.current) {
        setMyInspirations(userDesigns);
      }
    } catch (error) {
      if (!loadCancelledRef.current) {
        console.error('Error loading my inspirations:', error);
      }
    }
  };

  const handleRunGarmentAnalysis = useCallback(
    async (inspiration) => {
      if (!isAdmin || !inspiration?.imageUrl) return;
      setReanalyzingId(inspiration._id);
      try {
        const ar = await analyzeGarment(inspiration.imageUrl);
        if (ar?.success && ar.data?.analysis) {
          const updated = await updateInspirationalDesign(inspiration._id, {
            garmentAnalysis: {
              ...ar.data.analysis,
              analyzedAt: new Date().toISOString(),
              modelUsed: ar.data.modelUsed,
            },
          });
          setMyInspirations((prev) =>
            prev.map((item) =>
              String(item._id) === String(inspiration._id) ? { ...item, ...updated } : item
            )
          );
        }
      } catch (error) {
        console.error('Garment analysis error:', error);
        alert(error?.message || 'Garment analysis failed. Please try again.');
      } finally {
        setReanalyzingId(null);
      }
    },
    [isAdmin]
  );

  const loadDesigns = async () => {
    setLoading(true);
    try {
      // PNGs from public/Female — see src/data/femaleInspirationDesigns.js
      setDesigns(FEMALE_INSPIRATION_DESIGNS);
    } catch (error) {
      console.error('Error loading designs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Show all designs without filtering
  const filteredDesigns = designs;
  const filteredMyInspirations = myInspirations;

  const virtualGarmentCollections = useMemo(
    () =>
      buildVirtualGarmentCollections(myInspirations, {
        includeEmptyTypes: showEmptyGarmentTypes,
      }),
    [myInspirations, showEmptyGarmentTypes]
  );

  const groupedVirtualCollections = useMemo(
    () => groupVirtualCollectionsByCategory(virtualGarmentCollections),
    [virtualGarmentCollections]
  );

  const openCollection = useMemo(() => {
    if (!selectedCollectionKey) return null;
    return virtualGarmentCollections.find((c) => c.key === selectedCollectionKey) || null;
  }, [virtualGarmentCollections, selectedCollectionKey]);

  const handleDownload = (design) => {
    // Create a temporary link to download the image
    const link = document.createElement('a');
    link.href = design.imageUrl;
    link.download = `${design.name || design.productName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderGarmentUploadLoader = () => {
    const current = bulkUploadProgress.current || 1;
    const total = bulkUploadProgress.total || 1;
    const progress = Math.min(100, Math.max(10, Math.round((current / total) * 100)));

    return (
      <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/75 p-5 backdrop-blur-md">
        <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-slate-950 via-purple-950 to-fuchsia-950 p-6 text-white shadow-2xl">
          <div className="absolute -left-12 -top-12 h-40 w-40 animate-pulse rounded-full bg-fuchsia-400/30 blur-3xl" />
          <div className="absolute -right-10 top-10 h-36 w-36 animate-pulse rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="absolute bottom-3 left-1/2 h-24 w-24 -translate-x-1/2 animate-ping rounded-full bg-white/10" />
          <div className="relative text-center">
            <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-3xl bg-white/10 shadow-inner ring-1 ring-white/20">
              <div className="absolute h-16 w-16 animate-spin rounded-full border-2 border-white/20 border-t-white" />
              <Sparkles className="h-8 w-8 text-white drop-shadow" />
            </div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-fuchsia-100">
              Garment upload studio
            </p>
            <h3 className="mt-2 text-xl font-bold">
              Creating your garment library
            </h3>
            <p className="mt-2 truncate text-sm text-white/70" title={currentUploadLabel}>
              {currentUploadLabel || 'Preparing garment design...'}
            </p>
            <div className="mt-6">
              <div className="mb-2 flex items-center justify-between text-xs text-white/70">
                <span>{total > 1 ? `Design ${current} of ${total}` : 'Single design'}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-white/15">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-cyan-300 via-fuchsia-300 to-amber-200 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-2 text-[11px] font-semibold text-white/75">
              <div className="rounded-xl bg-white/10 px-2 py-2">Upload</div>
              <div className="rounded-xl bg-white/10 px-2 py-2">Thumbnail</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const uploadGarmentDesignFile = async (file, productName) => {
    setCurrentUploadLabel(`Uploading ${file.name}`);
    const uploadResult = await uploadImageToS3(file, {
      folder: 'inspirational-designs',
      fileName: buildGarmentFileName(file.name),
    });
    if (!uploadResult.success) {
      throw new Error('Failed to upload image');
    }

    let thumbnailUrl = '';
    try {
      setCurrentUploadLabel(`Creating thumbnail for ${file.name}`);
      const thumbnailFile = await createThumbnailImageFile(file);
      const thumbnailUpload = await uploadImageToS3(thumbnailFile, {
        folder: 'inspirational-designs/thumbnails',
        fileName: buildThumbnailFileName(file.name),
      });
      if (thumbnailUpload.success) {
        thumbnailUrl = thumbnailUpload.url;
      }
    } catch (thumbnailError) {
      console.warn('Garment thumbnail creation/upload failed; falling back to original image:', thumbnailError);
    }

    return createInspirationalDesign({
      imageUrl: uploadResult.url,
      thumbnailUrl,
      productName,
    });
  };

  // Handle adding new inspirational design
  const handleAddInspiration = async () => {
    const selectedFiles = Array.isArray(newDesign.imageFiles) && newDesign.imageFiles.length > 0
      ? newDesign.imageFiles
      : newDesign.imageFile
        ? [newDesign.imageFile]
        : [];
    const isBulkUpload = selectedFiles.length > 1;

    if (selectedFiles.length === 0) {
      alert('Please select one or more images');
      return;
    }

    setUploadingDesign(true);
    setBulkUploadProgress({ current: 0, total: selectedFiles.length || 1 });
    setCurrentUploadLabel('Preparing upload...');
    try {
      for (let index = 0; index < selectedFiles.length; index += 1) {
        const file = selectedFiles[index];
        setBulkUploadProgress({ current: index + 1, total: selectedFiles.length });

        const productName = getFileBaseName(file);

        await uploadGarmentDesignFile(file, productName);
      }

      setNewDesign({ ...EMPTY_NEW_DESIGN });
      setShowAddModal(false);
      await loadMyInspirations();
      alert(isBulkUpload ? 'Garment designs added successfully.' : 'Garment design added successfully.');
    } catch (error) {
      console.error('Error adding inspirational design:', error);
      alert('Failed to add inspirational design. Please try again.');
    } finally {
      setUploadingDesign(false);
      setBulkUploadProgress({ current: 0, total: 0 });
      setCurrentUploadLabel('');
    }
  };

  // Handle deleting inspiration
  const handleDeleteInspiration = async (id) => {
    if (!window.confirm('Are you sure you want to delete this inspiration?')) {
      return;
    }

    try {
      await deleteInspirationalDesign(id);
      await loadMyInspirations();
      alert('Inspiration deleted successfully!');
    } catch (error) {
      console.error('Error deleting inspiration:', error);
      alert('Failed to delete inspiration. Please try again.');
    }
  };

  const handleToggleVisibility = useCallback(async (inspiration, makePublic) => {
    setVisibilityLoadingId(inspiration._id);
    try {
      const updated = await updateInspirationalDesign(inspiration._id, { isPublic: makePublic });
      setMyInspirations((prev) =>
        prev.map((item) => (item._id === inspiration._id ? { ...item, ...updated } : item))
      );
    } catch (error) {
      console.error('Error updating garment design visibility:', error);
      alert('Failed to update visibility. Please try again.');
    } finally {
      setVisibilityLoadingId(null);
    }
  }, []);

  const renderMakePublicRow = (inspiration) => {
    const isVisibilityLoading = visibilityLoadingId === inspiration._id;
    return (
      <div
        className="flex items-center justify-between text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col min-w-0 pr-2">
          <span className="text-gray-500 font-medium">Make it public</span>
          {inspiration.isPublic && (
            <span className="text-[10px] text-gray-400">Visible on your public link</span>
          )}
        </div>
        <label
          className={`relative inline-flex shrink-0 items-center cursor-pointer ${
            isVisibilityLoading ? 'opacity-60 cursor-not-allowed' : ''
          }`}
        >
          <input
            type="checkbox"
            className="sr-only peer"
            checked={!!inspiration.isPublic}
            onChange={(e) => handleToggleVisibility(inspiration, e.target.checked)}
            disabled={isVisibilityLoading}
          />
          <div className="w-10 h-5 bg-gray-300 rounded-full transition-colors duration-200 peer-checked:bg-green-500" />
          <span className="absolute left-0.5 top-0.5 h-4 w-4 bg-white rounded-full shadow transition-transform duration-200 peer-checked:translate-x-5" />
        </label>
      </div>
    );
  };

  const renderTypeCollectionCard = (collection) => {
    const count = collection.designs?.length || 0;
    const isEmpty = count === 0;
    return (
      <button
        type="button"
        key={collection.key}
        onClick={() => setSelectedCollectionKey(collection.key)}
        className={`text-left bg-white rounded-xl shadow-sm border hover:shadow-md transition-all p-4 ${
          isEmpty ? 'border-gray-100 opacity-60' : 'border-purple-100'
        }`}
        title="View designs in this type"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-800 truncate">{collection.name}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {count} design{count === 1 ? '' : 's'}
            </p>
          </div>
          <Layers size={18} className="text-purple-600 shrink-0" />
        </div>
      </button>
    );
  };

  const renderGroupedTypeCollections = () => (
    <div className="space-y-8">
      {groupedVirtualCollections.map((section) => (
        <section key={section.key}>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            {section.label}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.collections.map((collection) => renderTypeCollectionCard(collection))}
          </div>
        </section>
      ))}
    </div>
  );

  const renderCollectionDesignCard = (d) => (
    <div
      key={d._id}
      role="button"
      tabIndex={0}
      onClick={() => d._id && setGarmentDetailId(String(d._id))}
      onKeyDown={(e) => {
        if (!d._id) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setGarmentDetailId(String(d._id));
        }
      }}
      className="bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer text-left"
    >
      <div className="aspect-square bg-gray-100 relative overflow-hidden">
        <GarmentCardImage design={d} className="absolute inset-0" />
      </div>
      <div className="p-3">
        <h3 className="font-semibold text-gray-800 text-sm truncate">{d.productName}</h3>
        <p className="text-xs text-gray-500 mt-1">{new Date(d.createdAt).toLocaleDateString()}</p>
        {isAdmin && d.user ? (
          <p className="text-xs text-violet-600 mt-1 truncate font-medium" title={designOwnerLabel(d)}>
            {designOwnerLabel(d)}
          </p>
        ) : null}
        <div className="mt-2">{renderMakePublicRow(d)}</div>
        <GarmentAnalysisSummary analysis={d.garmentAnalysis} />
        {isAdmin && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleRunGarmentAnalysis(d);
            }}
            disabled={reanalyzingId === d._id}
            className="mt-2 w-full text-xs font-medium text-purple-700 hover:text-purple-900 py-1.5 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            {reanalyzingId === d._id ? (
              <>
                <Loader2 size={14} className="animate-spin shrink-0" />
                Analyzing…
              </>
            ) : (
              <>
                <Sparkles size={14} className="shrink-0" />
                {d.garmentAnalysis?.productCategory ? 'Re-run analysis' : 'Run analysis'}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );

  // Handle file selection
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setNewDesign({
        ...newDesign,
        imageFile: files[0],
        imageFiles: files
      });
    }
  };

  const showGarmentTabs = !publicBrowseOnly;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading inspirational designs...</p>
        </div>
      </div>
    );
  }

  if (garmentDetailId && !publicBrowseOnly) {
    return (
      <div className="w-full min-h-0 min-w-0 max-w-full overflow-x-hidden bg-gradient-to-br from-purple-50 via-fuchsia-50/30 to-pink-50 py-2 pb-[max(5rem,calc(2.5rem+env(safe-area-inset-bottom)))] md:min-h-screen md:py-6">
        <GarmentDesignDetailView
          designId={garmentDetailId}
          onBack={() => setGarmentDetailId(null)}
          showNotification={showNotification}
          setActiveView={setActiveView}
          setProductDetails={setProductDetails}
          onDesignUpdated={(updated) => {
            if (!updated?._id) return;
            const id = String(updated._id);
            setMyInspirations((prev) =>
              prev.map((item) => (String(item._id) === id ? { ...item, ...updated } : item))
            );
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Garment designs</h1>
              <p className="text-gray-600">
                {publicBrowseOnly
                  ? 'Explore our collection of female fashion designs for inspiration'
                  : isAdmin
                    ? 'View all user-uploaded garment designs'
                    : 'Manage your garment designs, analysis, and AI kits generated from each'}
              </p>
            </div>
            {!publicBrowseOnly && garmentPageTab === 'designs' && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl"
              >
                <Plus size={20} />
                <span className="hidden sm:inline">Add designs</span>
                <span className="sm:hidden">Add</span>
              </button>
            )}
          </div>
        </div>

        {showGarmentTabs && (
          <div className="flex space-x-1 bg-white rounded-lg p-1 mb-6 shadow-sm border border-purple-100">
            <button
              type="button"
              onClick={() => {
                setGarmentPageTab('designs');
                setSelectedCollectionKey(null);
              }}
              className={`flex-1 py-3 px-4 rounded-md font-medium transition-all flex items-center justify-center gap-2 ${
                garmentPageTab === 'designs'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <LayoutGrid size={18} />
              <span>Designs</span>
            </button>
            <button
              type="button"
              onClick={() => setGarmentPageTab('collections')}
              className={`flex-1 py-3 px-4 rounded-md font-medium transition-all flex items-center justify-center gap-2 ${
                garmentPageTab === 'collections'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <Layers size={18} />
              <span>Collections</span>
            </button>
          </div>
        )}

        {/* Designs Grid / Collections */}
        {publicBrowseOnly ? (
          filteredDesigns.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-12 text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Eye size={24} className="text-purple-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">No designs available</h3>
              <p className="text-gray-600">Check back later for inspirational designs</p>
            </div>
          ) : (
            <ProgressiveGrid
              items={filteredDesigns}
              getItemKey={(design) => design.id}
              className={viewMode === 'grid'
                ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6'
                : 'space-y-4'}
              renderItem={(design) => (
              <div
                className={`bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden hover:shadow-lg transition-all duration-300 group ${
                  viewMode === 'list' ? 'flex items-center space-x-4 p-4' : ''
                }`}
              >
                {viewMode === 'grid' ? (
                  <>
                    <div className="aspect-square bg-gray-100 relative overflow-hidden">
                      <GarmentCardImage
                        design={design}
                        className="absolute inset-0"
                        imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      
                      {/* Action Buttons */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleDownload(design)}
                            className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                            title="Download"
                          >
                            <Download size={16} className="text-gray-700" />
                          </button>
                          <button
                            className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                            title="View"
                          >
                            <Eye size={16} className="text-gray-700" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-gray-800 text-sm truncate">{design.name}</h3>
                      <p className="text-xs text-gray-500 mt-1">{design.category}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      <GarmentCardImage design={design} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 truncate">{design.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{design.category} • {design.subcategory}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleDownload(design)}
                        className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors"
                        title="Download"
                      >
                        <Download size={16} />
                      </button>
                      <button
                        className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors"
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </>
                )}
              </div>
              )}
            />
          )
        ) : garmentPageTab === 'collections' ? (
          <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-4 sm:p-6">
            <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
              <div className="min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <Layers size={20} className="text-purple-600 shrink-0" />
                  <span className="truncate">
                    {openCollection ? openCollection.name : 'Browse by garment type'}
                  </span>
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {openCollection
                    ? `${openCollection.designs?.length || 0} designs matching this product type`
                    : 'Collections are filters by product category and type from garment analysis (e.g. Women · Dress, Men · Blazer).'}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                {openCollection ? (
                  <button
                    type="button"
                    onClick={() => setSelectedCollectionKey(null)}
                    className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                    title="Back to all types"
                  >
                    <ArrowLeft size={16} />
                    <span className="hidden sm:inline">All types</span>
                  </button>
                ) : (
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                      checked={showEmptyGarmentTypes}
                      onChange={(e) => setShowEmptyGarmentTypes(e.target.checked)}
                    />
                    <span>Show all catalog types</span>
                  </label>
                )}
              </div>
            </div>

            {openCollection ? (
              (openCollection.designs?.length || 0) === 0 ? (
                <div className="py-10 text-center">
                  <p className="text-gray-700 font-medium">No designs in this type yet.</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Upload garments and run analysis, or assign designs with matching{' '}
                    {openCollection.typeLabel || 'product type'} metadata.
                  </p>
                </div>
              ) : (
                <ProgressiveGrid
                  items={openCollection.designs || []}
                  getItemKey={(d) => d._id || d.id}
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6"
                  renderItem={(d) => renderCollectionDesignCard(d)}
                />
              )
            ) : virtualGarmentCollections.length === 0 ? (
              <div className="py-10 text-center">
                <p className="text-gray-700 font-medium">No garment types to show yet.</p>
                <p className="text-sm text-gray-500 mt-1">
                  Upload designs and run garment analysis, or enable “Show all catalog types” to browse empty
                  categories.
                </p>
              </div>
            ) : (
              renderGroupedTypeCollections()
            )}
          </div>
        ) : (
          filteredMyInspirations.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-purple-100 p-12 text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus size={24} className="text-purple-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {myInspirations.length === 0 
                  ? (isAdmin ? 'No user designs yet' : 'No inspirations yet')
                  : 'No matching inspirations'}
              </h3>
              <p className="text-gray-600 mb-6">
                {myInspirations.length === 0 
                  ? (isAdmin 
                      ? 'Use the button above to add a design (it is saved under your admin account), or browse designs uploaded by users.'
                      : 'Start building your collection by adding inspirational designs')
                  : 'Try adjusting your search criteria'}
              </p>
              {myInspirations.length === 0 && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl"
                >
                  <Plus size={20} />
                  <span>Add designs</span>
                </button>
              )}
            </div>
          ) : (
            <ProgressiveGrid
              items={filteredMyInspirations}
              getItemKey={(inspiration) => inspiration._id}
              className={viewMode === 'grid'
                ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-6'
                : 'space-y-4'}
              renderItem={(inspiration) => (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => inspiration._id && setGarmentDetailId(String(inspiration._id))}
                  onKeyDown={(e) => {
                    if (!inspiration._id) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setGarmentDetailId(String(inspiration._id));
                    }
                  }}
                  className={`bg-white rounded-xl shadow-sm border border-purple-100 overflow-hidden hover:shadow-lg transition-all duration-300 group text-left cursor-pointer ${
                    viewMode === 'list' ? 'flex flex-col gap-3 p-4' : ''
                  }`}
                >
                  {viewMode === 'grid' ? (
                    <>
                      <div className="aspect-square bg-gray-100 relative overflow-hidden">
                        <GarmentCardImage
                          design={inspiration}
                          className="absolute inset-0"
                          imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        
                        {/* Action Buttons */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <div className="flex space-x-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(inspiration);
                              }}
                              className="p-2 bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors"
                              title="Download"
                            >
                              <Download size={16} className="text-gray-700" />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteInspiration(inspiration._id);
                              }}
                              className="p-2 bg-red-500 rounded-full shadow-lg hover:bg-red-600 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={16} className="text-white" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="font-semibold text-gray-800 text-sm truncate">{inspiration.productName}</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(inspiration.createdAt).toLocaleDateString()}
                        </p>
                        <div className="mt-2">{renderMakePublicRow(inspiration)}</div>
                        <GarmentAnalysisSummary analysis={inspiration.garmentAnalysis} />
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRunGarmentAnalysis(inspiration);
                            }}
                            disabled={reanalyzingId === inspiration._id}
                            className="mt-2 w-full text-xs font-medium text-purple-700 hover:text-purple-900 py-1.5 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50"
                          >
                            {reanalyzingId === inspiration._id ? (
                              <>
                                <Loader2 size={14} className="animate-spin shrink-0" />
                                Analyzing…
                              </>
                            ) : (
                              <>
                                <Sparkles size={14} className="shrink-0" />
                                {inspiration.garmentAnalysis?.productCategory ? 'Re-run analysis' : 'Run analysis'}
                              </>
                            )}
                          </button>
                        )}
                        {isAdmin && inspiration.user && (
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            {inspiration.user.name || inspiration.user.email || 'Unknown User'}
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center space-x-4 w-full">
                      <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <GarmentCardImage design={inspiration} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-800 truncate">{inspiration.productName}</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Added {new Date(inspiration.createdAt).toLocaleDateString()}
                        </p>
                        {isAdmin && inspiration.user && (
                          <p className="text-xs text-gray-400 mt-1 truncate">
                            {inspiration.user.name || inspiration.user.email || 'Unknown User'}
                          </p>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(inspiration);
                          }}
                          className="p-2 bg-purple-50 text-purple-600 rounded-lg hover:bg-purple-100 transition-colors"
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteInspiration(inspiration._id);
                          }}
                          className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      </div>
                      <div className="w-full pl-0 sm:pl-20">{renderMakePublicRow(inspiration)}</div>
                      <div className="w-full pl-0 sm:pl-20">
                        <GarmentAnalysisSummary analysis={inspiration.garmentAnalysis} />
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRunGarmentAnalysis(inspiration);
                            }}
                            disabled={reanalyzingId === inspiration._id}
                            className="mt-2 text-xs font-medium text-purple-700 hover:text-purple-900 py-1.5 px-3 rounded-lg border border-purple-200 hover:bg-purple-50 transition-colors inline-flex items-center gap-1.5 disabled:opacity-50"
                          >
                            {reanalyzingId === inspiration._id ? (
                              <>
                                <Loader2 size={14} className="animate-spin shrink-0" />
                                Analyzing…
                              </>
                            ) : (
                              <>
                                <Sparkles size={14} className="shrink-0" />
                                {inspiration.garmentAnalysis?.productCategory ? 'Re-run analysis' : 'Run analysis'}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            />
          )
        )}

        {/* Add Inspiration Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              {uploadingDesign ? renderGarmentUploadLoader() : null}
              <div className="flex justify-between items-start gap-3 mb-6">
                <div className="min-w-0 pr-2">
                  <h2 className="text-2xl font-bold text-gray-800">Add garment design</h2>
                  <p className="text-sm text-gray-500 mt-2 leading-snug">
                    Upload one or many garment images. Garment analysis is not run automatically.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setNewDesign({ ...EMPTY_NEW_DESIGN });
                    setBulkUploadProgress({ current: 0, total: 0 });
                  }}
                  className="text-gray-400 hover:text-gray-600 shrink-0"
                  disabled={uploadingDesign}
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload Image(s) <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-500 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      id="inspiration-image-upload"
                      disabled={uploadingDesign}
                    />
                    <label 
                      htmlFor="inspiration-image-upload" 
                      className="cursor-pointer"
                    >
                      {newDesign.imageFiles?.length > 1 ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-3 gap-2">
                            {newDesign.imageFiles.slice(0, 6).map((file) => (
                              <div key={`${file.name}-${file.lastModified}`} className="aspect-square overflow-hidden rounded-lg bg-gray-100">
                                <BlobPreview
                                  file={file}
                                  alt={file.name}
                                  className="h-full w-full object-cover"
                                />
                              </div>
                            ))}
                          </div>
                          <p className="text-sm font-medium text-gray-700">{newDesign.imageFiles.length} files selected</p>
                          <p className="text-xs text-gray-500">
                            {newDesign.imageFiles.slice(0, 4).map((file) => file.name).join(', ')}
                            {newDesign.imageFiles.length > 4 ? ` +${newDesign.imageFiles.length - 4} more` : ''}
                          </p>
                          <p className="text-xs text-purple-600">Click to change selection</p>
                        </div>
                      ) : newDesign.imageFile ? (
                        <div className="space-y-2">
                          <BlobPreview
                            file={newDesign.imageFile}
                            alt="Preview"
                            className="w-full h-48 object-cover rounded-lg mb-2"
                          />
                          <p className="text-sm text-gray-600">{newDesign.imageFile.name}</p>
                          <p className="text-xs text-purple-600">Click to change</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                            <Plus size={24} className="text-purple-600" />
                          </div>
                          <p className="text-sm text-gray-600">Click to upload one or more images</p>
                          <p className="text-xs text-gray-400">PNG, JPG, GIF up to 10MB each</p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowAddModal(false);
                      setNewDesign({ ...EMPTY_NEW_DESIGN });
                      setBulkUploadProgress({ current: 0, total: 0 });
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    disabled={uploadingDesign}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddInspiration}
                    disabled={uploadingDesign}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {uploadingDesign ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        <span>
                          {bulkUploadProgress.total > 1
                            ? `Uploading ${bulkUploadProgress.current}/${bulkUploadProgress.total}…`
                            : 'Saving design…'}
                        </span>
                      </>
                    ) : (
                      <span>Add designs</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InspirationalDesignsView;
